/**
 * The signature catalog, read for ASSEMBLY rather than for browsing.
 *
 * The Slide Templates tab already fetches this same `*.signatures.json`
 * manifest for its card grid (slide-templates/services/slideTemplatesApi.ts)
 * and normalises each entry into a UI shape — name, category, wireframe
 * preview, source-file URL. That shape deliberately DROPS the raw
 * `placeholders` array, which is exactly what patching a template needs: the
 * backend's slot ids ("body#0", "body#1") are derived from those placeholders
 * in reading order, so the add-in has to re-derive the identical ordering
 * over the identical input to know which physical shape each id refers to
 * (see service/templateAssembler.ts).
 *
 * So this module reuses the tab's SAS mint (`requestSlideTemplatesSasInfo` —
 * the one authenticated step, pane-only) and downloads the manifests again
 * itself, keeping the raw entries. It is only ever called at generate time,
 * in the pane.
 *
 * EVERY LIBRARY THE RANKER CAN SCORE OVER
 * DeckorateAPI ranks over EVERY folder in `CATALOG_BLOB_NAMES`
 * (`transform-layouts` + `standard-layouts`), so a recommendation can name a
 * template in either. `blob-sas` mints ONE folder per call, keyed by
 * `template_type` — and the non-AI backend's 0002_SeedClientAssets.sql maps
 * "Standard" to `standard-layouts` and "Template" to `transform-layouts`.
 * So this fetches BOTH, merges their entries into one id-keyed map, and keeps
 * each library's SAS against its folder name. A pick then resolves to a URL
 * through the SAS for its OWN folder, which the catalog id encodes
 * ("transform-layouts/Cover_Slide.pptx#2").
 *
 * One library failing does NOT fail the fetch: an unregistered or
 * unauthorised row leaves that folder's picks unresolvable (reported per
 * slide at assembly time) while the other library still works. Only a total
 * failure throws.
 *
 * Once the backend returns an explicit blob location per slide (see
 * templatesApi.ts's `templateBlobRef`, which already prefers a ready
 * `sas_url`) none of this folder bookkeeping is needed any more.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import { directorySasFileUrl, sasVersionHeaders } from "../../shared/services/blobImageUpload";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import {
  logSlideTemplatesError,
  requestSlideTemplatesSasInfo,
  type SlideTemplateLibraryType,
  type SlideTemplatesSasInfo,
} from "../../slide-templates/services/slideTemplatesApi";

/**
 * Every registered library the create-slides flow reads, in preference order
 * (the first wins a duplicated id). "Template" is transform-layouts, where
 * the ranker's cover/list/closing layouts actually live.
 */
export const TEMPLATE_LIBRARY_TYPES: readonly SlideTemplateLibraryType[] = ["Standard", "Template"];

/** "transform-layouts/Cover_Slide.pptx#2" → "transform-layouts". */
export function folderOfTemplateId(templateId: string): string | null {
  const slash = templateId.indexOf("/");
  return slash > 0 ? templateId.slice(0, slash) : null;
}

/** ".../slides/standard-layouts/standard-layouts.signatures.json" →
 *  "standard-layouts" — the folder a minted directory SAS actually covers. */
export function folderOfManifestPath(manifestBlobPath: string): string | null {
  const segments = manifestBlobPath.split("/").filter(Boolean);
  return segments.length >= 2 ? segments[segments.length - 2]! : null;
}

const MANIFEST_DOWNLOAD_FAILED_MESSAGE = "Downloading the slide template library failed.";
const MANIFEST_NOT_JSON_MESSAGE = "The slide template library response wasn't valid JSON.";

/** 16:9 in EMU — the fallback when an entry declares no slide dimensions.
 *  Mirrors `_DEFAULT_SLIDE_DIMS` in DeckorateAPI's slot_manifest.py, because
 *  the reading-order sort buckets rows by a percentage of slide HEIGHT and
 *  must bucket them the same way the backend did. */
export const DEFAULT_SLIDE_DIMENSIONS: SlideDimensionsEmu = { cx: 12192000, cy: 6858000 };

export interface SlideDimensionsEmu {
  readonly cx: number;
  readonly cy: number;
}

export interface PlaceholderBBox {
  readonly x: number;
  readonly y: number;
  readonly cx: number;
  readonly cy: number;
}

/**
 * One placeholder exactly as the signature manifest describes it. `name` is
 * the shape's own OOXML name (`<p:cNvPr name="...">`) — the join key back to
 * the live .pptx — and `kind` is "sp" for a text shape (anything else is a
 * picture/table/chart the fitter never writes text into).
 */
export interface CatalogPlaceholder {
  readonly role: string;
  readonly name: string | null;
  readonly kind: string | null;
  readonly bbox: PlaceholderBBox | null;
}

/** One catalog entry, keeping everything assembly needs. */
export interface CatalogEntry {
  readonly id: string;
  /** Filename of the source .pptx within the library folder, e.g. "Bio_Layout.pptx". */
  readonly pptxFile: string | null;
  /** 1-based position of this entry's slide inside `pptxFile`. */
  readonly slideIndex: number | null;
  readonly slideDimensions: SlideDimensionsEmu;
  readonly placeholders: readonly CatalogPlaceholder[];
}

/** The parsed libraries: every entry by catalog id, plus the directory SAS
 *  covering each folder, so a pick resolves through its OWN library. */
export interface TemplateCatalog {
  readonly entriesById: ReadonlyMap<string, CatalogEntry>;
  /** Folder name ("standard-layouts") → that library's directory SAS. */
  readonly sasByFolder: ReadonlyMap<string, SlideTemplatesSasInfo>;
}

function toFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function toBBox(raw: unknown): PlaceholderBBox | null {
  if (typeof raw !== "object" || raw === null) return null;
  const rect = raw as Record<string, unknown>;
  const x = toFiniteNumber(rect.x);
  const y = toFiniteNumber(rect.y);
  const cx = toFiniteNumber(rect.cx);
  const cy = toFiniteNumber(rect.cy);
  if (x === null || y === null || cx === null || cy === null) return null;
  return { x, y, cx, cy };
}

/**
 * One raw placeholder → a `CatalogPlaceholder`. Returns null for a
 * placeholder with no `role`, matching the backend's own filter
 * (`classified = [p for p in placeholders if p.get("role")]`) — an unclassified
 * placeholder is not part of the slot numbering on either side, so dropping it
 * here is what keeps the indices aligned.
 */
function toCatalogPlaceholder(raw: unknown): CatalogPlaceholder | null {
  if (typeof raw !== "object" || raw === null) return null;
  const placeholder = raw as Record<string, unknown>;
  const role = toNonEmptyString(placeholder.role);
  if (!role) return null;
  return {
    role,
    name: toNonEmptyString(placeholder.name),
    kind: toNonEmptyString(placeholder.kind),
    bbox: toBBox(placeholder.bbox ?? placeholder.xfrm),
  };
}

function toSlideDimensions(raw: unknown): SlideDimensionsEmu {
  if (typeof raw !== "object" || raw === null) return DEFAULT_SLIDE_DIMENSIONS;
  const dims = raw as Record<string, unknown>;
  const cx = toFiniteNumber(dims.cx);
  const cy = toFiniteNumber(dims.cy);
  if (cx === null || cy === null || cx <= 0 || cy <= 0) return DEFAULT_SLIDE_DIMENSIONS;
  return { cx, cy };
}

function toCatalogEntry(raw: unknown): CatalogEntry | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;
  const id = toNonEmptyString(entry.id);
  if (!id) return null;

  const rawPlaceholders = Array.isArray(entry.placeholders) ? entry.placeholders : [];
  const placeholders = rawPlaceholders
    .map(toCatalogPlaceholder)
    .filter((placeholder): placeholder is CatalogPlaceholder => placeholder !== null);

  return {
    id,
    pptxFile: toNonEmptyString(entry.pptxFile ?? entry.pptx_file),
    slideIndex: toFiniteNumber(entry.slideIndex ?? entry.slide_index),
    slideDimensions: toSlideDimensions(entry.slideDimensionsEmu ?? entry.slide_dimensions_emu),
    placeholders,
  };
}

/** Manifest root → its entry list, accepting the same three shapes the
 *  Slide Templates tab's own normaliser accepts. */
function manifestItems(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  const root = raw as { entries?: unknown; templates?: unknown } | null;
  if (Array.isArray(root?.entries)) return root.entries;
  if (Array.isArray(root?.templates)) return root.templates;
  logSlideTemplatesError(
    "create-slide-catalog-manifest-shape",
    "Manifest root was neither an array nor { entries: [] } / { templates: [] }",
  );
  return [];
}

async function downloadManifest(url: string, signal: AbortSignal | undefined): Promise<unknown> {
  const res = await fetch(url, {
    method: "GET",
    // A user-delegation SAS must echo its own `sv` back as x-ms-version or
    // Azure rejects it — see blobImageUpload.ts's SAS_VERSION_HEADER doc.
    headers: sasVersionHeaders(url, logSlideTemplatesError, "create-slide-catalog-sas-version-parse"),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) {
    const errText = await res.text().catch((error: unknown) => {
      logSlideTemplatesError(
        "create-slide-catalog-body-read",
        getErrorMessage(error, "Failed to read manifest error body"),
      );
      return "";
    });
    throw new Error(`${MANIFEST_DOWNLOAD_FAILED_MESSAGE} (${res.status} ${errText})`);
  }
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (error) {
    logSlideTemplatesError("create-slide-catalog-parse", getErrorMessage(error, "Manifest JSON.parse failed"));
    throw new Error(MANIFEST_NOT_JSON_MESSAGE);
  }
}

interface LoadedLibrary {
  readonly folder: string;
  readonly sasInfo: SlideTemplatesSasInfo;
  readonly entries: readonly CatalogEntry[];
}

/** Mints one library's SAS and parses its manifest. */
async function loadLibrary(
  http: HttpClient,
  options: HttpRequestOptions,
  templateType: SlideTemplateLibraryType,
): Promise<LoadedLibrary> {
  const sasInfo = await requestSlideTemplatesSasInfo(http, options, templateType);
  const manifestUrl = directorySasFileUrl(sasInfo.directorySasUrl, sasInfo.manifestBlobPath, (message) =>
    logSlideTemplatesError("create-slide-catalog-sas-directory-rebuild", message),
  );
  const manifest = await downloadManifest(manifestUrl, options.signal);
  const entries = manifestItems(manifest)
    .map(toCatalogEntry)
    .filter((entry): entry is CatalogEntry => entry !== null);

  // The manifest path is authoritative for the folder the SAS covers; an
  // entry's own id prefix is the fallback for a manifest stored flat.
  const folder =
    folderOfManifestPath(sasInfo.manifestBlobPath) ??
    (entries[0] ? folderOfTemplateId(entries[0].id) : null);
  if (!folder) throw new Error(`Couldn't tell which folder the ${templateType} library covers.`);
  return { folder, sasInfo, entries };
}

/**
 * Mints a directory SAS for every library in `TEMPLATE_LIBRARY_TYPES` and
 * parses each manifest, keeping the raw placeholders. Pane-only — the mints
 * need an authorised `HttpClient`.
 *
 * Duplicate ids are kept first-wins rather than suffixed: unlike the card
 * grid (where a duplicate id would make two cards ambiguous to click), the
 * ids here are looked up by what the OUTLINE endpoint returned, and the
 * backend's own catalog is keyed by the same raw id — so a suffixed variant
 * would simply never be asked for.
 */
export async function fetchTemplateCatalog(
  http: HttpClient,
  options: HttpRequestOptions = {},
): Promise<TemplateCatalog> {
  const results = await Promise.allSettled(
    TEMPLATE_LIBRARY_TYPES.map((templateType) => loadLibrary(http, options, templateType)),
  );

  const entriesById = new Map<string, CatalogEntry>();
  const sasByFolder = new Map<string, SlideTemplatesSasInfo>();
  for (const [index, result] of results.entries()) {
    if (result.status === "rejected") {
      // Isolated on purpose: the other library's picks must still build.
      logSlideTemplatesError(
        "create-slide-catalog-library-failed",
        `${TEMPLATE_LIBRARY_TYPES[index]} library unavailable: ${getErrorMessage(result.reason, "unknown error")}`,
      );
      continue;
    }
    const { folder, sasInfo, entries } = result.value;
    sasByFolder.set(folder, sasInfo);
    for (const entry of entries) if (!entriesById.has(entry.id)) entriesById.set(entry.id, entry);
  }

  // Every library failed — there's nothing to assemble from at all, so
  // surface the first real reason rather than an empty catalog that would
  // fail identically on every slide.
  if (sasByFolder.size === 0) {
    const rejection = results.find((result) => result.status === "rejected");
    throw rejection?.status === "rejected"
      ? (rejection.reason as Error)
      : new Error(MANIFEST_DOWNLOAD_FAILED_MESSAGE);
  }
  return { entriesById, sasByFolder };
}

/** The catalog entry for `templateId`, or null when no loaded library holds it. */
export function resolveCatalogEntry(catalog: TemplateCatalog, templateId: string): CatalogEntry | null {
  return catalog.entriesById.get(templateId) ?? null;
}

/**
 * A fetchable URL for `fileName` inside `folder`, built from that folder's own
 * directory SAS — which grants read access to every sibling blob under it.
 *
 * Returns null when no loaded library covers that folder, so a caller never
 * resolves a file against the WRONG library's token: that would 404, or worse
 * fetch a same-named file out of the other folder.
 */
export function fileUrlInFolder(
  catalog: TemplateCatalog,
  folder: string | null,
  fileName: string,
): string | null {
  const sasInfo = folder ? catalog.sasByFolder.get(folder) : undefined;
  if (!sasInfo) return null;
  return directorySasFileUrl(sasInfo.directorySasUrl, fileName, (message) =>
    logSlideTemplatesError("create-slide-catalog-source-url-rebuild", message),
  );
}

/**
 * A fetchable URL for an entry's source `.pptx`, resolved through the SAS for
 * the entry's own folder. Null when the entry carries no `pptxFile` (nothing
 * to fetch — a caller must not fall back to the manifest URL) or when that
 * folder's library didn't load.
 */
export function entrySourceFileUrl(catalog: TemplateCatalog, entry: CatalogEntry): string | null {
  if (!entry.pptxFile) return null;
  return fileUrlInFolder(catalog, folderOfTemplateId(entry.id), entry.pptxFile);
}

/**
 * A fetchable URL for a blob path the BACKEND named, rather than one derived
 * from a catalog entry — used once the fit response carries an explicit
 * location (templatesApi.ts's `templateBlobRef`). The path's own parent
 * folder decides which library's SAS signs it, falling back to
 * `fallbackFolder` (the template id's folder) for a bare filename.
 */
export function blobPathFileUrl(
  catalog: TemplateCatalog,
  blobPath: string,
  fallbackFolder: string | null,
): string | null {
  const segments = blobPath.split("/").filter(Boolean);
  const fileName = segments[segments.length - 1];
  if (!fileName) return null;
  const pathFolder = segments.length >= 2 ? segments[segments.length - 2]! : null;
  const folder = pathFolder && catalog.sasByFolder.has(pathFolder) ? pathFolder : fallbackFolder;
  return fileUrlInFolder(catalog, folder, fileName);
}
